import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormatCurrency from 'hooks/useFormatCurrency';
import ToggleButton from 'components/button/ToggleButton';
import InfoIcon from 'components/icons/InfoIcon';
import AmountField from 'components/domain/lead/modal/tabs/details/order/fields/common/AmountField';
import { FeeAmountTypeEnum } from 'models/PropertyFees';
import {
  OrderViewPriceDetailsButtonWrapper,
  OrderViewPriceDetailsListError,
  OrderViewPriceDetailsListItemRowWrapper,
} from './OrderViewPriceDetailsListItemRow.styles';
import EditOrderAdjustmentButton from './actions/EditOrderAdjustmentButton';
import DeleteOrderAdjustmentButton from './actions/DeleteOrderAdjustmentButton';

export interface PriceDetailsItem {
  currency: string;
  label: string | React.ReactNode;
  tooltip?: string;
  amount: number;
  isEditable?: boolean;
  amountType?: FeeAmountTypeEnum;
  isTotalAmount?: boolean;
  breakdowns?: Pick<PriceDetailsItem, 'label' | 'amount'>[];
  onEdit?: () => void;
  onDelete?: () => void;
  onItemUpdated?: (val: number) => void;
  hasError?: boolean;
}

const OrderViewPriceDetailsListItemRow = ({
  currency,
  label,
  tooltip,
  amount,
  amountType,
  isTotalAmount,
  breakdowns,
  isEditable,
  hasError,
  onEdit,
  onDelete,
  onItemUpdated,
  ...props
}: PriceDetailsItem) => {
  const formatCurrency = useFormatCurrency();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const { t } = useTranslation();

  return (
    <div {...props}>
      <OrderViewPriceDetailsListItemRowWrapper
        $hasBorder={!isTotalAmount}
        $isCapitalized={isTotalAmount}
        $isBold={isTotalAmount}
      >
        <div data-testid="price-details-list-item-title">
          <span>{label}</span>
          {tooltip && (
            <InfoIcon
              id="property-channel-widget-info"
              tooltipPlacement="bottom"
              tooltipText={tooltip}
            />
          )}
          <span>
            {/*
             // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
            {breakdowns?.length > 0 && (
              <ToggleButton
                data-testid="price-details-list-item-toggle-button"
                isExpanded={showBreakdown}
                toggleVertically
                onClick={() => setShowBreakdown(!showBreakdown)}
              />
            )}
          </span>
          <OrderViewPriceDetailsButtonWrapper>
            {onEdit && <EditOrderAdjustmentButton onEdit={onEdit} />}
            {onDelete && <DeleteOrderAdjustmentButton onDelete={onDelete} />}
          </OrderViewPriceDetailsButtonWrapper>
        </div>
        <div
          className="text-right"
          data-testid="price-details-list-item-amount"
        >
          {amountType === FeeAmountTypeEnum.AMOUNT ? (
            <AmountField
              value={amount}
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              isEditable={isEditable}
              currency={currency}
              // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              onItemUpdated={(val) => {
                // @ts-expect-error TS2722 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                onItemUpdated(val);
              }}
            />
          ) : (
            `${amount}%`
          )}
        </div>
        {hasError && (
          <OrderViewPriceDetailsListError>
            {t('componentOrder.duplicateFeeError')}
          </OrderViewPriceDetailsListError>
        )}
      </OrderViewPriceDetailsListItemRowWrapper>
      {showBreakdown &&
        breakdowns?.map((breakdown) => (
          <OrderViewPriceDetailsListItemRowWrapper
            key={breakdown.label as string}
            $isChildren
            $hasBorder
            $isBold={false}
          >
            <div>{breakdown.label} </div>
            <div className="text-right">
              {formatCurrency({
                value: breakdown.amount,
                currency,
              })}
            </div>
          </OrderViewPriceDetailsListItemRowWrapper>
        ))}
    </div>
  );
};

OrderViewPriceDetailsListItemRow.defaultProps = {
  isTotalAmount: false,
  breakdowns: [],
  amountType: FeeAmountTypeEnum.AMOUNT,
  tooltip: null,
  onEdit: null,
  onDelete: null,
  isEditable: false,
  onItemUpdated: () => {},
  hasError: false,
};

export default OrderViewPriceDetailsListItemRow;
